import { useEffect, useState } from "react";
import { apiRequest } from "../../services/api";
import SettlementReversal from "./SettlementReversal";

export default function SettlementPostingPreview({
  data,
  companyId,
  accessToken,
  run,
  busy,
  canManage,
  requestKey,
  canWrite,
  canReverse,
  onPosted,
}) {
  const [workspace, setWorkspace] = useState(null),
    [plan, setPlan] = useState(null),
    [reviewNote, setReviewNote] = useState(""),
    [checks, setChecks] = useState({}),
    [posted, setPosted] = useState(null);
  useEffect(() => {
    setWorkspace(null);
    setPlan(null);
    setChecks({});
    setReviewNote("");
    setPosted(null);
  }, [data, companyId]);
  const input = "block w-full rounded-lg border p-2 mt-1";
  const eligible = data.evidence.filter(
    (e) =>
      !e.supersededId &&
      !e.posting &&
      e.remainingGross === "0.00" &&
      !data.channels.find((c) => c.id === e.channelId)?.retiredId,
  );
  function submit(event) {
    event.preventDefault();
    const f = Object.fromEntries(new FormData(event.currentTarget));
    const { evidenceId, ...body } = f;
    setPlan(null);
    setChecks({});
    setPosted(null);
    run(
      () =>
        apiRequest(
          `/accounting/${companyId}/settlement-evidence/${evidenceId}/posting-preview`,
          { method: "POST", accessToken, body },
        ),
      setPlan,
    );
  }
  function post() {
    if (
      !canWrite ||
      busy ||
      !plan ||
      reviewNote.trim().length < 20 ||
      ![
        "bankStatementReviewed",
        "priorEntriesReviewed",
        "expenseTreatmentReviewed",
        "correctionRestrictionAccepted",
      ].every((k) => checks[k] === true)
    )
      return;
    const body = {
      yearId: plan.yearId,
      feeSetupId: plan.feeSetupId,
      postingDate: plan.postingDate,
      previewHash: plan.previewHash,
      reviewNote: reviewNote.trim(),
      ...checks,
    };
    body.requestKey = requestKey("settlement-posting", body);
    run(
      async () => {
        const result = await apiRequest(
          `/accounting/${companyId}/settlement-evidence/${plan.evidenceId}/posting`,
          { method: "POST", accessToken, body },
        );
        return result;
      },
      (result) => {
        setPosted(result);
        setPlan(null);
        setChecks({});
      },
    );
  }
  return (
    <section className="mt-6 rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold">Bank settlement posting review</h3>
      <p className="my-2 text-sm text-slate-600">
        Preview fully allocated credits, then explicitly review before recording
        a settlement voucher. No bank transfer is made. Fee tax is expensed, not
        claimed as GST input credit.
      </p>
      {!workspace ? (
        <button
          type="button"
          disabled={busy}
          className="rounded-lg border px-3 py-2"
          onClick={() =>
            run(async () => {
              const [w, s] = await Promise.all([
                apiRequest(`/accounting/${companyId}/workspace`, {
                  accessToken,
                }),
                apiRequest(
                  `/accounting/${companyId}/settlement-evidence/fee-setups`,
                  { accessToken },
                ),
              ]);
              return { ...w, setups: s.setups };
            }, setWorkspace)
          }
        >
          Load posting review options
        </button>
      ) : (
        <form
          onSubmit={submit}
          onChange={() => setPlan(null)}
          className="grid gap-3 md:grid-cols-2"
        >
          <label>
            Fully allocated bank credit
            <select
              name="evidenceId"
              required
              className={input}
              defaultValue=""
            >
              <option value="">Select credit</option>
              {eligible.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.bankCreditReference} · gross {e.gross} · net {e.net}
                </option>
              ))}
            </select>
          </label>
          <label>
            Financial year
            <select name="yearId" required className={input} defaultValue="">
              <option value="">Select year</option>
              {workspace.years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Posting date
            <input type="date" name="postingDate" required className={input} />
          </label>
          <label>
            Reviewed fee setup
            <select
              name="feeSetupId"
              required
              className={input}
              defaultValue=""
            >
              <option value="">Select current company/GST setup</option>
              {workspace.setups
                .filter(
                  (s, i, all) =>
                    all.findIndex(
                      (x) => x.registrationId === s.registrationId,
                    ) === i,
                )
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {
                      workspace.registrations.find(
                        (r) => r.id === s.registrationId,
                      )?.name
                    }{" "}
                    · version {s.sequence}
                  </option>
                ))}
            </select>
          </label>
          <button
            disabled={busy || !eligible.length}
            className="rounded-lg border px-3 py-2"
          >
            Preview accounting entries
          </button>
        </form>
      )}
      {workspace && (
        <FeeSetup
          workspace={workspace}
          canManage={canManage}
          requestKey={requestKey}
          busy={busy}
          run={run}
          companyId={companyId}
          accessToken={accessToken}
          onSaved={(setups) => {
            setWorkspace({ ...workspace, setups });
            setPlan(null);
          }}
        />
      )}
      {plan && (
        <div className="mt-4">
          <p className="rounded-lg bg-amber-50 p-3 text-sm">{plan.warning}</p>
          <table className="mt-3 w-full text-left text-sm">
            <thead>
              <tr>
                <th>Ledger</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {plan.lines.map((l) => (
                <tr key={l.ledgerId}>
                  <td>{plan.ledgerNames[l.ledgerId]}</td>
                  <td>{l.debit}</td>
                  <td>{l.credit}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-sm">
            Sales revenue is not posted again. Posting freezes these
            allocations; generic reversal is unavailable.
          </p>
          {canWrite && (
            <div className="mt-3 space-y-3">
              <label className="block">
                Posting review note
                <textarea
                  className={input}
                  value={reviewNote}
                  maxLength={1000}
                  onChange={(e) => {
                    setReviewNote(e.target.value);
                    setChecks({});
                  }}
                />
              </label>
              {[
                [
                  "bankStatementReviewed",
                  "I reviewed the actual bank statement and credit reference.",
                ],
                [
                  "priorEntriesReviewed",
                  "I checked for prior/manual postings so this credit is not counted twice.",
                ],
                [
                  "expenseTreatmentReviewed",
                  "I reviewed the fees and fee-tax expense treatment; no GST ITC is claimed.",
                ],
                [
                  "correctionRestrictionAccepted",
                  "I accept that these allocations will be frozen and generic reversal is disabled.",
                ],
              ].map(([key, label]) => (
                <label className="block text-sm" key={key}>
                  <input
                    type="checkbox"
                    checked={!!checks[key]}
                    onChange={(e) =>
                      setChecks({ ...checks, [key]: e.target.checked })
                    }
                  />{" "}
                  {label}
                </label>
              ))}
              <button
                type="button"
                className="rounded-lg bg-slate-900 px-4 py-2 text-white disabled:opacity-40"
                disabled={
                  busy ||
                  reviewNote.trim().length < 20 ||
                  Object.values(checks).filter(Boolean).length !== 4
                }
                onClick={post}
              >
                Record reviewed settlement voucher
              </button>
            </div>
          )}
        </div>
      )}
      {workspace && (
        <SettlementReversal
          data={data}
          workspace={workspace}
          companyId={companyId}
          accessToken={accessToken}
          run={run}
          busy={busy}
          canWrite={canReverse}
          requestKey={requestKey}
          onSaved={onPosted}
        />
      )}
      {posted && (
        <div role="status" className="mt-3 rounded-lg bg-green-50 p-3">
          Settlement voucher {posted.voucher?.number || posted.voucherId}{" "}
          recorded. No bank transfer was made.{" "}
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              run(
                () =>
                  apiRequest(`/accounting/${companyId}/settlement-evidence`, {
                    accessToken,
                  }),
                (r) => onPosted?.(r),
              )
            }
          >
            Refresh settlement workspace
          </button>
        </div>
      )}
      <ul className="mt-3 text-sm">
        {data.evidence
          .filter((e) => e.posting)
          .map((e) => (
            <li key={e.id}>
              {e.bankCreditReference}: posted voucher{" "}
              {e.posting.voucher?.number} · allocations frozen
            </li>
          ))}
      </ul>
    </section>
  );
}

function FeeSetup({
  workspace,
  canManage,
  requestKey,
  busy,
  run,
  companyId,
  accessToken,
  onSaved,
}) {
  const [confirmed, setConfirmed] = useState(false);
  const input = "block w-full rounded-lg border p-2 mt-1";
  function save(event) {
    event.preventDefault();
    if (!canManage || !confirmed || busy) return;
    const form = Object.fromEntries(new FormData(event.currentTarget));
    const current = workspace.setups.find(
      (s) => s.registrationId === form.registrationId,
    );
    const body = {
      ...form,
      reviewNote: form.reviewNote.trim(),
      expenseTreatmentReviewed: true,
      ...(current ? { expectedSetupId: current.id } : {}),
    };
    body.requestKey = requestKey("fee-setup", body);
    run(
      async () => {
        await apiRequest(
          `/accounting/${companyId}/settlement-evidence/fee-setups`,
          { accessToken, method: "POST", body },
        );
        return apiRequest(
          `/accounting/${companyId}/settlement-evidence/fee-setups`,
          { accessToken },
        );
      },
      (result) => {
        onSaved(result.setups);
        setConfirmed(false);
      },
    );
  }
  return (
    <details className="mt-4 rounded-lg border p-3">
      <summary className="cursor-pointer font-semibold">
        Reviewed fee settings and history
      </summary>
      <p className="my-2 text-sm">
        Configuration only. Versions are retained. Fee tax is expensed, not
        claimed as GST input credit. An accountant must review this treatment.
      </p>
      {canManage && (
        <form
          onSubmit={save}
          onChange={() => setConfirmed(false)}
          className="grid gap-3 md:grid-cols-2"
        >
          <label>
            GST registration
            <select
              required
              name="registrationId"
              className={input}
              defaultValue=""
            >
              <option value="">Select registration</option>
              {workspace.registrations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          {[
            ["feesLedgerId", "Fee expense ledger"],
            ["feeTaxExpenseLedgerId", "Fee-tax expense ledger"],
          ].map(([name, label]) => (
            <label key={name}>
              {label}
              <select required name={name} className={input} defaultValue="">
                <option value="">Select dedicated expense ledger</option>
                {workspace.ledgers
                  .filter(
                    (l) =>
                      l.kind === "GENERAL" && l.group?.nature === "EXPENSE",
                  )
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </select>
            </label>
          ))}
          <label>
            Review note
            <textarea
              required
              minLength={20}
              maxLength={1000}
              name="reviewNote"
              className={input}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => {
                event.stopPropagation();
                setConfirmed(event.target.checked);
              }}
            />{" "}
            I reviewed the separate expense accounts and expense-only fee-tax
            treatment.
          </label>
          <button
            disabled={busy || !confirmed}
            className="rounded-lg border px-3 py-2"
          >
            Save reviewed fee version
          </button>
        </form>
      )}
      <ul className="mt-3 space-y-2 text-sm">
        {workspace.setups.map((s) => (
          <li key={s.id}>
            {
              workspace.registrations.find((r) => r.id === s.registrationId)
                ?.name
            }{" "}
            · v{s.sequence} ·{" "}
            {workspace.ledgers.find((l) => l.id === s.feesLedgerId)?.name ||
              s.feesLedgerId}{" "}
            /{" "}
            {workspace.ledgers.find((l) => l.id === s.feeTaxExpenseLedgerId)
              ?.name || s.feeTaxExpenseLedgerId}
            <br />
            {s.reviewNote}
            <br />
            Reviewed {s.createdAt} by {s.createdBy}
          </li>
        ))}
      </ul>
    </details>
  );
}
